import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom'
import styled from 'styled-components'
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined'
import DescriptionIcon from '@mui/icons-material/Description'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import { toast } from 'react-toastify'
import BackLink from 'components/BackLink'
import { SkeletonBlock, SkeletonLine } from 'components/Skeleton'
import LoadError from 'features/actions/workspace/LoadError'
import ActionsAPI from 'services/ActionsAPI'
import { RAG } from 'features/dashboard/charts/scoreColour'
import {
  getActionRoutine,
  reopenActionRoutine,
  selectCurrentAction,
  selectMutationLoading,
  updateActionRoutine,
} from 'features/actions/ducks'
import GuruInsightModal from 'features/bpDetails/Components/files/GuruInsightModal'
import StagePill from 'features/actions/workspace/StagePill'
import OwnerCell from 'features/actions/workspace/OwnerCell'
import DueCell from 'features/actions/workspace/DueCell'
import { OverdueExplainer } from 'features/actions/workspace/Explainers'
import { canRejectAiFind, severityMeta, sourceLabel } from 'features/actions/workspace/remedialMeta'
import { formatDay } from 'features/actions/workspace/remedialDates'
import { PrimaryButton } from 'features/actions/workspace/ui'
import ConvertToTaskDialog from './ConvertToTaskDialog'
import CompleteActionDialog from './CompleteActionDialog'
import DismissActionDialog from './DismissActionDialog'
import EditActionDialog from './EditActionDialog'
import ActionActivityTimeline from './ActionActivityTimeline'
import RemedialTasksPanel from './RemedialTasksPanel'
import ImportedRemedialDetails from './ImportedRemedialDetails'
import RemedialFiles from './RemedialFiles'
import { PRIVATE_PATHS, redirect } from 'utils/paths'
import { getHubReturn, hasInAppHistory } from 'utils/hubReturnPath'

const formatDateTime = iso => {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

const ActionDetail = () => {
  const { id } = useParams()
  const location = useLocation()
  const reactRouterNavigate = useNavigate()
  const dispatch = useDispatch()
  const action = useSelector(selectCurrentAction)
  const mutating = useSelector(selectMutationLoading)
  const loadError = useSelector(state => state.actions?.currentActionError)
  const loading = useSelector(state => state.actions?.currentActionLoading)

  const [convertOpen, setConvertOpen] = useState(false)
  const [dismissOpen, setDismissOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [completeOpen, setCompleteOpen] = useState(false)
  const [completing, setCompleting] = useState(false)
  const [insightFile, setInsightFile] = useState(null)

  useEffect(() => { dispatch(getActionRoutine(id)) }, [dispatch, id])

  const wasMutating = useRef(false)
  useEffect(() => {
    if (wasMutating.current && !mutating) dispatch(getActionRoutine(id))
    wasMutating.current = mutating
  }, [mutating, dispatch, id])

  const isLoaded = action && action.id === id
  const sev = isLoaded ? severityMeta(action.severity) : null
  const perms = isLoaded ? action.permissions || {} : {}

  const openLinkedFile = file => {
    if (!file?.id || !action.buildingPassport?.id) return
    setInsightFile({
      id: file.id,
      displayName: file.displayName,
      buildingPassportId: action.buildingPassport.id,
      buildingPassportName: action.buildingPassport.name,
    })
  }

  const openSourceFile = () => {
    if (!action.sourceFile?.id || !action.buildingPassport?.id) return
    setInsightFile({
      id: action.sourceFile.id,
      displayName: action.sourceFile.displayName,
      buildingPassportId: action.buildingPassport.id,
      buildingPassportName: action.buildingPassport.name,
    })
  }

  const confirmComplete = async () => {
    if (!action || completing) return
    setCompleting(true)
    try {
      await ActionsAPI.resolve(action.id)
      toast.success('Remedial marked complete.')
      setCompleteOpen(false)
      dispatch(getActionRoutine(id))
    } catch (e) {
      toast.error('Could not mark this remedial complete. Please try again.')
    } finally {
      setCompleting(false)
    }
  }

  const changeOwner = (target, ownerUserId) => dispatch(updateActionRoutine({ id: target.id, ownerUserId }))
  const changeDue = (target, dueDate) => dispatch(updateActionRoutine({ id: target.id, dueDate }))

  return (
    <Page>
      <BackLink
        onClick={() => {
          if (hasInAppHistory(location)) reactRouterNavigate(-1)
          else redirect(getHubReturn(PRIVATE_PATHS.actions || '/actions'))
        }}
      >
        Back to remedials
      </BackLink>

      {!isLoaded && loadError && !loading ? (
        <LoadError
          text='We couldn’t open this remedial. It may have been removed, or you may not have access to its Building Passport.'
          onRetry={() => dispatch(getActionRoutine(id))}
        />
      ) : !isLoaded ? (
        <SkeletonCard aria-busy='true' aria-label='Loading remedial'>
          <SkeletonLine $w='30%' $h={14} />
          <SkeletonLine $w='70%' $h={26} />
          <SkeletonBlock $h={140} />
          <SkeletonBlock $h={90} />
        </SkeletonCard>
      ) : (
        <>
          <Header>
            <PipRow>
              {sev && <Pip $colour={sev.color} $bg={sev.bg}>{sev.label}</Pip>}
              <StagePill stage={action.stage} action={action} withExplainer />
              {action.isOverdue && (
                <>
                  <Pip $colour={RAG.critical.fg} $bg={RAG.critical.bg}>Overdue</Pip>
                  <OverdueExplainer />
                </>
              )}
            </PipRow>
            <Title>{action.title}</Title>
            <MetaRow>
              {action.buildingPassport?.name && (
                <Meta>
                  <ApartmentOutlinedIcon style={{ fontSize: 16 }} />
                  {action.buildingPassport.name}
                </Meta>
              )}
              <Meta>{sourceLabel(action.source)}</Meta>
            </MetaRow>
          </Header>

          <Card>
            {action.description && (
              <Section>
                <Label>Description</Label>
                <Body>{action.description}</Body>
              </Section>
            )}

            <Section>
              <Label>Who and when</Label>
              <KvList>
                <KvRow><Kv>Owner</Kv><KvValue><OwnerCell action={action} onChange={changeOwner} /></KvValue></KvRow>
                <KvRow><Kv>Due</Kv><KvValue><DueCell action={action} onChange={changeDue} /></KvValue></KvRow>
                {action.suggestedDueBy && action.suggestedDueBy !== action.dueDate && (
                  <KvRow><Kv>Date suggested by the source</Kv><KvValue>{formatDay(action.suggestedDueBy)}</KvValue></KvRow>
                )}
              </KvList>
            </Section>

            <RemedialTasksPanel action={action} onAddTask={() => setConvertOpen(true)} onChanged={() => dispatch(getActionRoutine(id))} />

            {action.files?.length > 0 && (
              <Section>
                <RemedialFiles files={action.files} onOpen={openLinkedFile} />
              </Section>
            )}

            {action.metadata?.imported && (
              <Section>
                <ImportedRemedialDetails action={action} />
              </Section>
            )}

            <Section>
              <Label>Source</Label>
              <SourceRow>
                {action.sourceFile?.displayName && action.buildingPassport?.id ? (
                  <SourceButton type='button' onClick={openSourceFile}>
                    <DescriptionIcon style={{ fontSize: 16 }} />
                    <span>{action.sourceFile.displayName}</span>
                    {action.sourcePageRef && <SourcePage>page {action.sourcePageRef}</SourcePage>}
                    <OpenInNewIcon style={{ fontSize: 12, opacity: 0.7 }} />
                  </SourceButton>
                ) : action.source === 'incident' && action.sourceIncidentId ? (
                  <IncidentLink as={Link} to={`/incidents/${action.sourceIncidentId}`}>
                    <ReportProblemOutlinedIcon style={{ fontSize: 16 }} />
                    From an incident report
                    <OpenInNewIcon style={{ fontSize: 12, opacity: 0.7 }} />
                  </IncidentLink>
                ) : (
                  <SourcePlain>{sourceLabel(action.source)}</SourcePlain>
                )}
              </SourceRow>
            </Section>

            <Section>
              <Label>History</Label>
              <KvList>
                <KvRow><Kv>Created</Kv><KvValue>{formatDateTime(action.createdAt) || 'Not recorded'}</KvValue></KvRow>
                {action.updatedAt && action.updatedAt !== action.createdAt && (
                  <KvRow><Kv>Last updated</Kv><KvValue>{formatDateTime(action.updatedAt)}</KvValue></KvRow>
                )}
                {action.responsibleContractor && (
                  <KvRow>
                    <Kv>Contractor</Kv>
                    <KvValue>
                      <ContractorLink href={`/tasks/${action.responsibleContractor.taskId}`}>
                        {action.responsibleContractor.name}
                        {action.responsibleContractor.company ? ` (${action.responsibleContractor.company})` : ''}
                      </ContractorLink>
                      <ContractorHint>, through its task</ContractorHint>
                    </KvValue>
                  </KvRow>
                )}
                {action.dismissedReason && (
                  <KvRow><Kv>Reason for dismissal</Kv><KvValue>{action.dismissedReason}</KvValue></KvRow>
                )}
              </KvList>
            </Section>

            <ActionBar>
              {action.stage === 'needs_decision' && perms.canConvert && (
                <PrimaryButton type='button' onClick={() => setConvertOpen(true)}>
                  <CheckCircleOutlineIcon style={{ fontSize: 18 }} />
                  Create task
                </PrimaryButton>
              )}
              {perms.canResolve && (
                <SecondaryBtn type='button' onClick={() => setCompleteOpen(true)}>Mark as complete</SecondaryBtn>
              )}
              {perms.canUpdate && (
                <SecondaryBtn type='button' onClick={() => setEditOpen(true)}>
                  <EditOutlinedIcon style={{ fontSize: 18 }} />
                  Edit
                </SecondaryBtn>
              )}
              {canRejectAiFind(action) && (
                <SecondaryBtn type='button' onClick={() => setDismissOpen(true)}>Not a real issue</SecondaryBtn>
              )}
              {action.stage === 'dismissed' && perms.canReopen && (
                <PrimaryButton type='button' onClick={() => dispatch(reopenActionRoutine(action.id))} disabled={mutating}>
                  <RestartAltIcon style={{ fontSize: 18 }} />
                  Reopen remedial
                </PrimaryButton>
              )}
            </ActionBar>

            <Section>
              <ActionActivityTimeline actionId={action.id} />
            </Section>
          </Card>
        </>
      )}

      <ConvertToTaskDialog action={action} open={convertOpen} onClose={() => setConvertOpen(false)} />
      <DismissActionDialog action={action} open={dismissOpen} onClose={() => setDismissOpen(false)} />
      <EditActionDialog action={action} open={editOpen} onClose={() => setEditOpen(false)} />
      <CompleteActionDialog
        action={action}
        open={completeOpen}
        onClose={() => setCompleteOpen(false)}
        submitting={completing}
        onConfirm={confirmComplete}
      />
      {insightFile && (
        <GuruInsightModal
          open
          onClose={() => setInsightFile(null)}
          file={insightFile}
          bpId={insightFile.buildingPassportId}
          buildingPassportName={insightFile.buildingPassportName}
        />
      )}
    </Page>
  )
}

export default ActionDetail

const Page = styled.div`
  max-width: 880px;
  margin: 0 auto;
  padding: 30px 24px;
  @media (max-width: 768px) { padding: 18px 16px; }
`

const SkeletonCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 24px;
  background: #fff;
  border: 1px solid #e4e4e7;
  border-radius: 12px;
`

const Header = styled.div`
  margin-bottom: 18px;
`

const PipRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
`

const Pip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${({ $bg }) => $bg || '#e2e8f0'};
  color: ${({ $colour }) => $colour || '#475569'};
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: currentColor;
  }
`

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0 0 8px 0;
  line-height: 1.3;
`

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: center;
`

const Meta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 15px;
  color: ${({ $danger, theme }) => $danger ? '#b00020' : (theme.colors.grey?.[800] || '#64748b')};
  font-weight: ${({ $danger }) => $danger ? 600 : 'inherit'};
`

const Card = styled.div`
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.04);
  border-radius: 12px;
  padding: 24px 28px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  @media (max-width: 600px) { padding: 18px 18px; }
`

const Section = styled.div`
  margin: 0 0 22px 0;
  padding-top: 18px;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};
  &:first-child { margin-top: 0; padding-top: 0; border-top: none; }
  &:last-of-type { margin-bottom: 0; }
`

const Label = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#64748b'};
  font-weight: 600;
  margin-bottom: 8px;
`

const Body = styled.div`
  white-space: pre-wrap;
  font-size: 14px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.haiti};
`

const SourceRow = styled.div`
  display: flex;
  align-items: center;
`

const SourceButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #ede9fe;
  color: #5b21b6;
  border: none;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  &:hover { background: #ddd6fe; }
`

const SourcePage = styled.span`
  font-size: 11px;
  font-weight: 500;
  background: rgba(255,255,255,0.6);
  padding: 1px 6px;
  border-radius: 999px;
  color: #5b21b6;
`

const IncidentLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fee2e2;
  color: #b91c1c;
  border: 1px solid #fecaca;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  &:hover { background: #fecaca; }
`

const SourcePlain = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
`

const KvList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const KvRow = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  font-size: 15px;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
`

const Kv = styled.div`
  width: 190px;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#64748b'};
`

const KvValue = styled.div`
  flex: 1;
  color: ${({ theme }) => theme.colors.haiti};
`

const ContractorLink = styled.a`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
  text-decoration: underline;
  &:hover { color: #0C1229; }
`

const ContractorHint = styled.span`
  margin-left: 6px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#64748b'};
`

const ActionBar = styled.div`
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const SecondaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  background: white;
  color: ${({ theme }) => theme.colors.haiti};
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: ${({ theme }) => theme.colors.lightGrey}; }
`
